import { Fragment, useMemo, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { parseMarkdown, type Block, type Inline } from './parse-markdown';

/**
 * Markdown fara HTML (B3, spec §6). Totul iese ca elemente React: un `<script>` din sursa e TEXT,
 * escapat de React. Nu adauga `dangerouslySetInnerHTML` aici.
 *
 * - `mode="block"` (implicit): titluri, liste, paragrafe; newline simplu -> `<br />`.
 * - `mode="inline"`: doar bold/cursiv, intr-un `<span>`; blocurile se unesc prin spatiu (pentru
 *   descrieri scurte, taiate la 2 randuri, pe carduri). Linkurile devin textul lor: descrierile
 *   inline stau in butoane (StepChooseActivity) sau pe carduri, iar un `<a>` intr-un `<button>` e
 *   HTML invalid si declanseaza la un singur tap si linkul, si butonul (fix round 1, review Task 6).
 */
export function Markdown({
  source,
  mode = 'block',
  className,
}: {
  source?: string | null;
  mode?: 'block' | 'inline';
  className?: string;
}): ReactElement | null {
  // Parsarea o singura data pe sursa, nu la fiecare randare (ex. la fiecare schimbare de stare din
  // InfoCompletion). Hook-ul sta inaintea oricarui return, ca ordinea hook-urilor sa nu varieze.
  const blocuri = useMemo(() => (source && source.trim() ? parseMarkdown(source) : []), [source]);
  if (blocuri.length === 0) return null;

  if (mode === 'inline') {
    // Blocurile se unesc cu „. " cand cel dinainte nu se termina in punctuatie (un titlu, de obicei),
    // altfel cu spatiu — altfel „## Ce faceți" + paragraf devenea „Ce faceți Strângeți…" (final review M3).
    const noduri = blocuri.flatMap((b, i) => {
      const bucati = inlineDinBloc(b);
      if (i === 0) return bucati;
      const separator = /[.!?:;…,]\s*$/.test(textFinal(inlineDinBloc(blocuri[i - 1]))) ? ' ' : '. ';
      return [{ t: 'text', v: separator } as Inline, ...bucati];
    });
    return <span className={className}>{randeazaInline(noduri, 'inline')}</span>;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {blocuri.map((b, i) => randeazaBloc(b, i))}
    </div>
  );
}

/** Textul de la coada unei secvente inline (coboara in bold/cursiv/link), ca sa vedem punctuatia finala. */
function textFinal(noduri: Inline[]): string {
  const ultim = noduri[noduri.length - 1];
  if (!ultim) return '';
  return ultim.t === 'text' ? ultim.v : textFinal(ultim.c);
}

function inlineDinBloc(b: Block): Inline[] {
  if (b.t === 'ul' || b.t === 'ol') {
    return b.items.flatMap((item, i) => (i === 0 ? item : [{ t: 'text', v: ' ' } as Inline, ...item]));
  }
  return b.c;
}

function randeazaBloc(b: Block, key: number): ReactNode {
  switch (b.t) {
    case 'h': {
      const Tag = (`h${b.level}`) as 'h2' | 'h3' | 'h4';
      const marime = b.level === 2 ? 'text-lg' : b.level === 3 ? 'text-base' : 'text-sm';
      return (
        <Tag key={key} className={cn(marime, 'font-semibold text-balance')}>
          {randeazaInline(b.c, 'block')}
        </Tag>
      );
    }
    case 'ul':
      return (
        // `text-left`: o lista centrata (ex. in StartScreen, care centreaza descrierea) arata stricat.
        <ul key={key} className="list-disc pl-5 space-y-1 text-left">
          {b.items.map((item, j) => <li key={j} className="text-pretty">{randeazaInline(item, 'block')}</li>)}
        </ul>
      );
    case 'ol':
      return (
        <ol key={key} start={b.start} className="list-decimal pl-5 space-y-1 text-left">
          {b.items.map((item, j) => <li key={j} className="text-pretty">{randeazaInline(item, 'block')}</li>)}
        </ol>
      );
    case 'p':
      return <p key={key} className="text-pretty">{randeazaInline(b.c, 'block')}</p>;
  }
}

function randeazaInline(noduri: Inline[], mode: 'block' | 'inline'): ReactNode {
  return noduri.map((n, i) => {
    switch (n.t) {
      case 'text':
        return <Fragment key={i}>{randeazaText(n.v, mode)}</Fragment>;
      case 'strong':
        return <strong key={i}>{randeazaInline(n.c, mode)}</strong>;
      case 'em':
        return <em key={i}>{randeazaInline(n.c, mode)}</em>;
      case 'link':
        // Inline, linkul devine textul lui (vezi comentariul componentei: <a> in <button>).
        if (mode === 'inline') return <Fragment key={i}>{randeazaInline(n.c, mode)}</Fragment>;
        return (
          <a
            key={i}
            href={n.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            {randeazaInline(n.c, mode)}
          </a>
        );
    }
  });
}

/** In bloc, newline simplu -> <br />; inline, devine spatiu. */
function randeazaText(v: string, mode: 'block' | 'inline'): ReactNode {
  if (!v.includes('\n')) return v;
  if (mode === 'inline') return v.replace(/\n/g, ' ');
  const randuri = v.split('\n');
  return randuri.map((r, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {r}
    </Fragment>
  ));
}
